/**
 * Phase 47 (2026-07-29) - Bank Statement Rows component for the Cash Position hero card.
 *
 * Renders:
 *   Row A - 30-day sparkline on the right side of the hero line (owned by the
 *           parent card via <SparklineTrend />; exported separately below).
 *   Row B - One <BankRow /> per active bank, sorted DESC by balance.
 *   Row C - Companion warning pill (Cash on Hand + Petty Cash) - rendered by
 *           the parent card since it already owns the companion chip.
 *
 * All colors come from theme tokens so the widget re-themes correctly in
 * Light / Dark / Eye Comfort / High Contrast.
 */
import { useMemo } from "react";
import { Building2 } from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, Tooltip,
} from "recharts";

const CHART_TOKENS = [
  "var(--chart-color-1)", "var(--chart-color-2)", "var(--chart-color-3)",
  "var(--chart-color-4)", "var(--chart-color-5)",
];

function fmtMoney(n) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function StatusPill({ status }) {
  const s = String(status || "active").toLowerCase();
  const map = {
    active: { bg: "var(--success-pale, #E3F5EC)", fg: "var(--success, #0E9F6E)" },
    blocked: { bg: "var(--warning-pale, #FEF3C7)", fg: "var(--warning, #B54700)" },
    inactive: { bg: "var(--bg-hover, #F1F5F9)", fg: "var(--text-muted, #64748B)" },
    closed: { bg: "var(--bg-hover, #F1F5F9)", fg: "var(--text-muted, #64748B)" },
  };
  const c = map[s] || map.active;
  return (
    <span
      className="inline-flex items-center rounded-full text-[10px] px-1.5 py-0.5 font-medium capitalize"
      style={{ backgroundColor: c.bg, color: c.fg }}
      data-testid={`bank-status-${s}`}
    >
      {s}
    </span>
  );
}

function BankRow({ account, idx, isLast }) {
  const balance = Number(account?.balance || 0);
  const isZero = Math.abs(balance) < 0.005;
  const isNeg = balance < -0.005;
  const badgeColor = isZero ? "var(--text-muted, #64748B)" : CHART_TOKENS[idx % CHART_TOKENS.length];
  const badgeOpacity = isZero ? 0.30 : 1.0;
  const balanceColor = isZero
    ? "var(--text-muted)"
    : isNeg ? "var(--danger)" : "var(--text-primary)";

  return (
    <div
      className={`flex items-center gap-3 py-2 ${isLast ? "" : "border-b"}`}
      style={isLast ? {} : { borderColor: "var(--border-default, #E4EBF4)" }}
      data-testid={`bank-row-${idx}`}
      data-bank-code={account?.code || ""}
      data-bank-balance={balance.toFixed(2)}
    >
      {/* Left badge */}
      <span
        className="inline-flex h-7 w-7 items-center justify-center rounded-full shrink-0"
        style={{
          backgroundColor: badgeColor,
          opacity: badgeOpacity,
        }}
      >
        <Building2 className="h-3.5 w-3.5" style={{ color: "#FFFFFF" }} />
      </span>
      {/* Middle: name + meta */}
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm font-semibold truncate ${isZero ? "" : ""}`}
          style={{ color: isZero ? "var(--text-secondary)" : "var(--text-primary)" }}
          data-testid={`bank-row-${idx}-name`}
        >
          {account?.name || "Unnamed bank"}
        </p>
        <div className="mt-0.5 flex items-center gap-2 flex-wrap">
          <StatusPill status={account?.status} />
          {account?.account_number_masked && (
            <span
              className="text-[10px] tabular-nums"
              style={{ color: "var(--text-muted)" }}
              data-testid={`bank-row-${idx}-masked`}
            >
              {account.account_number_masked}
            </span>
          )}
        </div>
      </div>
      {/* Right: balance */}
      <p
        className="text-sm font-semibold tabular-nums text-right shrink-0"
        style={{ color: balanceColor }}
        data-testid={`bank-row-${idx}-balance`}
      >
        {fmtMoney(balance)}
      </p>
    </div>
  );
}

/** Tooltip for the sparkline - only shows on the last-point hover. */
function SparkTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div
      className="rounded-md border px-2 py-1 text-[10px] shadow-md"
      style={{
        backgroundColor: "var(--bg-surface)",
        borderColor: "var(--border-default)",
        color: "var(--text-primary)",
      }}
    >
      Today {fmtMoney(p.value)}
    </div>
  );
}

/**
 * Custom dot: renders only at the last index of the sparkline. Module-scoped
 * so React doesn't treat it as a new component type each render.
 */
function LastPointDot(props) {
  const { cx, cy, index, __lastIdx } = props;
  if (index !== __lastIdx) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={3}
      fill="var(--accent, #1F4FA3)"
      stroke="var(--bg-surface, #FFFFFF)"
      strokeWidth={1.2}
    />
  );
}

/**
 * A tiny sparkline. Accepts `series: [{date, value}]` (30 pts recommended).
 */
export function SparklineTrend({ series, width = 140, height = 44 }) {
  const data = useMemo(() => (series || []).map((r) => ({
    date: r.date, value: Number(r.value || 0),
  })), [series]);
  const lastIdx = data.length ? data.length - 1 : -1;

  if (!data.length) {
    return (
      <div
        className="rounded-md border border-dashed"
        style={{ width, height, borderColor: "var(--border-default)" }}
        data-testid="cash-sparkline-empty"
      />
    );
  }

  return (
    <div
      style={{ width, height }}
      data-testid="cash-sparkline"
      data-points={data.length}
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 6, bottom: 2, left: 0 }}>
          <defs>
            <linearGradient id="cashSparkFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent, #1F4FA3)" stopOpacity={0.12} />
              <stop offset="100%" stopColor="var(--accent, #1F4FA3)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Tooltip content={<SparkTooltip />} cursor={{ stroke: "transparent" }} />
          <Area
            type="monotone"
            dataKey="value"
            stroke="var(--accent, #1F4FA3)"
            strokeWidth={1.5}
            fill="url(#cashSparkFill)"
            isAnimationActive={false}
            dot={<LastPointDot __lastIdx={lastIdx} />}
            activeDot={{ r: 4, fill: "var(--accent, #1F4FA3)" }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * The bank statement rows list. Accepts an accounts array; sorts DESC by
 * balance and renders one <BankRow /> per bank. Zero-balance banks are shown
 * with a muted/inactive treatment.
 */
export default function BankStatementRows({ accounts = [] }) {
  const rows = useMemo(() => {
    const list = (accounts || []).filter((a) => (a?.kind || "bank") === "bank");
    return [...list].sort((a, b) =>
      Number(b?.balance || 0) - Number(a?.balance || 0));
  }, [accounts]);

  if (!rows.length) {
    return (
      <div
        className="rounded-md border border-dashed p-3 text-xs text-center"
        style={{ borderColor: "var(--border-default)", color: "var(--text-muted)" }}
        data-testid="bank-rows-empty"
      >
        No active bank accounts
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="bank-statement-rows">
      {rows.map((a, i) => (
        <BankRow
          key={a?.code || `row-${i}`}
          account={a}
          idx={i}
          isLast={i === rows.length - 1}
        />
      ))}
    </div>
  );
}
